import { useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  MultilineTextInput,
  Select,
  SelectOption,
} from '@hivewatch/dark'

import {
  IncidentDocument,
  useCreateIncidentActionMutation,
  useTechniciansQuery,
} from '@/graphql/generated/hooks'
import {
  DeviceType,
  IncidentActionType,
  IncidentStatus,
} from '@/graphql/generated/schemas'
import { BUTTON_PRESS } from '@/utils/analytics'
import { getTechnicianOptions } from '@/utils/forms/optionBuilders'

import { MixpanelDataIProps } from '../../../../shared/types/types'

type ColorScheme = 'dark' | 'light'

const MAX_MESSAGE_LENGTH = 255

interface OrderRepairIProps {
  incidentId: string
  facilityId: string
  deviceType: DeviceType
  incidentStatus: IncidentStatus
  /**
   * Whether ordering a repair is still permitted (in-progress + Device Health +
   * owner). The parent unmounts this panel when it flips false, but the submit
   * path guards on it too so a request can't fire if ownership is lost mid-edit.
   */
  isEnabled?: boolean
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
  colorScheme?: ColorScheme
}

export const OrderRepair = ({
  incidentId,
  facilityId,
  deviceType,
  incidentStatus,
  isEnabled = true,
  mixpanelData,
  onActionComplete,
  colorScheme = 'dark',
}: OrderRepairIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentActions } = usePermissions()
  const { showSuccess, showError } = useToasts()

  const [technician, setTechnician] = useState<SelectOption | null>(null)
  const [orderMessage, setOrderMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data, loading: isLoadingTechnicians } = useTechniciansQuery({
    fetchPolicy: 'network-only',
    // Don't pay for the query until we know which facility + device to scope to.
    skip: !facilityId || !deviceType,
    variables: {
      filter: {
        facilityIdsContain: facilityId,
        deviceTypesContain: deviceType,
      },
    },
  })
  const technicianOptions = getTechnicianOptions(data)

  const [createIncidentAction] = useCreateIncidentActionMutation()

  // The incident must be acknowledged (anything other than Active) before a
  // repair can be ordered — mirrors the legacy OrderRepairForm gate.
  const isAcknowledged = incidentStatus !== IncidentStatus.Active

  const onCancel = () => {
    trackEvent(`${BUTTON_PRESS} Order Repair - Cancel`, mixpanelData)
    onActionComplete?.()
  }

  const onSubmit = async () => {
    // Guard against a submit that races ownership loss: the panel unmounts on
    // the next render, but a click already in flight must not place the order.
    if (!technician || !isEnabled) return
    const sendRequestEventName = `${BUTTON_PRESS} Order Repair - Send Request`
    startTimer(sendRequestEventName)
    setIsSubmitting(true)

    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.OrderDeviceRepair,
            technicianId: technician.value,
            message: orderMessage,
          },
        },
        // No awaitRefetchQueries: a failed refetch must not make an
        // already-committed order report as failed (apollo-data-fetching.md).
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
        ],
      })
      showSuccess(t('incidents:device_repair_ordered'))
      trackEvent(sendRequestEventName, mixpanelData)
      onActionComplete?.()
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'order_device_repair',
          feature: 'incident_drawer',
          surface: 'order_repair',
        },
        extra: { incidentId, technicianId: technician.value },
      })
      showError()
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDark = colorScheme === 'dark'
  const labelColor = isDark ? 'text.primary' : 'light.text.primary'
  const subtitleColor = isDark ? 'text.secondary' : 'light.text.secondary'

  const isSubmitDisabled =
    !technician ||
    orderMessage.trim() === '' ||
    !isAcknowledged ||
    !isEnabled ||
    !shouldEnableIncidentActions

  return (
    <Box display='flex' flexDirection='column' gap='16px' px='24px' py='16px'>
      <Box display='flex' flexDirection='column' gap='2px'>
        <Box
          color={labelColor}
          fontFamily="'Soehne Kraftig', sans-serif"
          fontSize='20px'
          fontWeight='500'
          lineHeight='28px'
        >
          {t('incidents:incident_drawer_order_repair_header')}
        </Box>
        <Box
          color={subtitleColor}
          fontFamily="'Soehne', sans-serif"
          fontSize='11px'
          lineHeight='16px'
        >
          {t('incidents:incident_drawer_order_repair_subheader')}
        </Box>
      </Box>

      <Select
        colorScheme={colorScheme}
        data-testid='incidentDrawer_orderRepair_technician'
        isLoading={isLoadingTechnicians}
        label={t('incidents:technician_label')}
        onChange={setTechnician}
        options={technicianOptions}
        placeholder={t('incidents:technician_placeholder')}
        value={technician}
      />

      <Box display='flex' flexDirection='column' gap='4px'>
        <MultilineTextInput
          colorScheme={colorScheme}
          data-testid='incidentDrawer_orderRepair_message'
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => setOrderMessage(e.target.value)}
          placeholder={t('incidents:order_message_placeholder')}
          value={orderMessage}
        />
        <Box
          alignSelf='flex-end'
          color={subtitleColor}
          data-testid='incidentDrawer_orderRepair_charCount'
          fontFamily="'Soehne', sans-serif"
          fontSize='11px'
          lineHeight='16px'
        >
          {`${orderMessage.length}/${MAX_MESSAGE_LENGTH}`}
        </Box>
      </Box>

      <Box display='flex' gap='8px' justifyContent='flex-end'>
        <Button
          colorScheme={colorScheme}
          data-testid='incidentDrawer_orderRepair_cancel'
          onClick={onCancel}
          size='md'
          variant='secondary'
        >
          {t('common:cancel')}
        </Button>
        <Button
          colorScheme={colorScheme}
          data-testid='incidentDrawer_orderRepair_submit'
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={onSubmit}
          size='md'
          variant='primary'
        >
          {t('incidents:send_request')}
        </Button>
      </Box>
    </Box>
  )
}
